import { connect } from 'pwa-helpers';
import { html } from "lit";
import { customElement, property, state } from 'lit/decorators.js'

import { ifDefined } from 'lit-html/directives/if-defined.js';
import { BaseComponent } from '../base-component/base-component';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { createMessage, getCatalogLabel } from '../../common/utils/util';
import { isSuccess, selectTotal, selectV2Request } from './store/search-v2.selectors';
import { selectSelectedCatalog } from '../search/store/search.selector';
import { SearchV2Request } from './interface/search-v2.interface';
import { booleanConverter } from '../../common/converters';


/**
 * An element to display the current page within the search results.
 * 
 * @csspart first - The <span> containing the number representing the index of the first result.
 * @csspart last - The <span> containing the number representing the index of the last result.
 * @csspart total - The <span> containing the number representing the total number of results.
 * @csspart term - The <span> containing the number representing the search term.
 */
@customElement('mhecomm-search-v2-pagination-message')
export class PaginationMessage extends connect(store)(BaseComponent) {

  /**
   * The message to display to the user when there are results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  message = 'Showing %first%-%last% of %total% Results Matching "%term%"';

  /**
   * The message to display to the user when there are no results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  emptyMessage = '0 Results Matching "%term%"';

  /**
   * The message to display to the user when no term was provided by a %term% appears in the message property.
   */
  @property()
  noTermMessage = 'Showing %first%-%last% of %total% Results';

  /**
   * Region Id should be '-' seperated as this will be used as region name.
   * Example: results-bar will be labeled as 'results bar'
   */
  @property()
  regionid = '';

  @property({ converter: booleanConverter, attribute: 'use-aria-live' })
  useAriaLive = false;

  @state()
  private compiledMessage: HTMLElement | undefined;

  @state()
  private compiledEmptyMessage: HTMLElement | undefined;

  @state()
  private request?: SearchV2Request;

  private readonly onSkipToResults = (e: Event) => this.focusRegion(e);

  renderComp() {
    return html`
    <style>
      [part="term"], [part="business-unit"] {
        font-weight: bold;
      }
    </style>
    <span
    id="results-info-label"
    class="visually-hidden">
      ${ifDefined(this.regionid ? this.regionid.split('-').join(' ') + ' ' : undefined)}
    </span>
    <div
    tabindex=${ifDefined(this.regionid ? "-1" : undefined)}
    role=${ifDefined(this.regionid ? "region" : undefined)}
    aria-labelledby=${ifDefined(this.regionid ? "results-info-label" : undefined)}
    aria-describedby=${ifDefined(this.regionid ? "results-info-description" : undefined)}
    data-automation-id=${ifDefined(this.regionid ? this.regionid : undefined)}
    id=${ifDefined(this.regionid ? this.regionid : undefined)}>
      <div
      aria-live=${ifDefined(this.useAriaLive ? "polite" : undefined)}
      role=${ifDefined(this.useAriaLive ? "status" : undefined)}
      aria-atomic=${ifDefined(this.useAriaLive ? "true" : undefined)}
      id="results-info-description" class="mb-0">
        ${this.compiledMessage ? this.compiledMessage : ''}
        ${this.compiledEmptyMessage ? this.compiledEmptyMessage : ''}
      </div>
    </div>
    `;
  }

  update(changedProperties: any) {
    super.update(changedProperties);
  }

  stateChanged(state: StoreState) {
    if (isSuccess(state)) {
      this.request = selectV2Request(state);
      const selectedCatalog = selectSelectedCatalog(state);
      const total = selectTotal(state) || 0;
      const term = this.request?.searchTerms ?? '';
      const start = this.request?.start ? this.request?.start : 1;
      const maxEnd = (this.request?.start ? this.request?.start : 1) + (this.request?.pageLength ? this.request?.pageLength - 1 : 0);
      const end = maxEnd > total ? total : maxEnd;
      this.compiledMessage = undefined;

      if (total > 0) {
        this.compiledEmptyMessage = undefined;
        if (!term && this.message.includes('%term%')) {
          this.compiledMessage = this.compileMessage(this.noTermMessage, start, end, total, term, selectedCatalog);
        } else {
          this.compiledMessage = this.compileMessage(this.message, start, end, total, term, selectedCatalog);
        }
      } else {
        this.compiledMessage = undefined;
        this.compiledEmptyMessage = this.compileMessage(this.emptyMessage, start, end, total, term, selectedCatalog);
      }
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.regionid) {
      this.addTrackedEventListener(window, 'skip-to-results', this.onSkipToResults);
    }
  }

  compileMessage(message: string, start: number, end: number, total: number, term: string, businessUnit?: string) {
    const values = {
      'first': start.toLocaleString(),
      'last': end.toLocaleString(),
      'total': total.toLocaleString(),
      'term': term,
      'business-unit': businessUnit ? getCatalogLabel(businessUnit) : ''
    };
    
    return createMessage(message, values);
  }

  focusRegion(e: Event) {
    const event = e as CustomEvent;
    if (this.regionid === event.detail.target) {
      this.shadowRoot?.getElementById(this.regionid)?.focus();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v2-pagination-message': PaginationMessage;
  }
}
